import { ReactNode, useEffect, useState } from 'react'
import { ApiError } from 'features/common'
import { styled } from '@mui/material'
import BasicPageContainer from '../BasicPageContainer'
import BasicPageTitle from './BasicPageTitle'
import { TypographyProps } from '@carto/meridian-ds/components'

const Header = styled('header', {
  shouldForwardProp: (prop: string) => !['headerSticky'].includes(prop)
})<{ headerSticky?: boolean }>(({ headerSticky, theme }) => ({
  position: 'sticky',
  backgroundColor: 'inherit',
  top: 0,
  zIndex: 5,
  borderBottom: `1px solid transparent`,
  transition: theme.transitions.create('border', {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.shortest
  }),

  ...(headerSticky && {
    backgroundColor: 'inherit',
    borderBottomColor: theme.palette.divider,

    '& .MuiDivider-root': {
      opacity: 0
    }
  })
}))

interface BasicPageHeaderProps {
  title?: ReactNode
  titleId?: string
  titleTypographyProps?: TypographyProps
  titleChildren?: ReactNode
  primaryAction?: ReactNode
  beforeTitleContent?: ReactNode
  afterTitleContent?: ReactNode
  backUrl?: string
  hasError?: ApiError | null // TODO VVG: hasError is not boolean
  headerClassName?: string
  contentClassName?: string
  afterTitleContentClassName?: string
}

function BasicPageHeader({
  title,
  titleId,
  titleTypographyProps,
  titleChildren,
  primaryAction,
  beforeTitleContent,
  afterTitleContent,
  backUrl,
  hasError,
  headerClassName,
  contentClassName,
  afterTitleContentClassName
}: BasicPageHeaderProps) {
  const [headerSticky, setHeaderSticky] = useState<boolean>(false)

  useEffect(() => {
    const main: HTMLElement | null = document.querySelector('main')
    if (!main) {
      return
    }

    const scrollListener = () => {
      setHeaderSticky(main.scrollTop > 2)
    }
    main.addEventListener('scroll', scrollListener)
    return () => {
      main?.removeEventListener('scroll', scrollListener)
    }
  }, [setHeaderSticky])

  return (
    <Header headerSticky={headerSticky} className={headerClassName}>
      {!hasError && beforeTitleContent && beforeTitleContent}

      <BasicPageContainer className={contentClassName}>
        <BasicPageTitle
          TypographyProps={titleTypographyProps}
          title={title}
          titleId={titleId}
          titleChildren={titleChildren}
          primaryAction={primaryAction}
          backUrl={backUrl}
          hasError={hasError}
        />
      </BasicPageContainer>

      {!hasError && afterTitleContent && (
        <BasicPageContainer className={afterTitleContentClassName}>{afterTitleContent}</BasicPageContainer>
      )}
    </Header>
  )
}

export default BasicPageHeader
